/* eslint-disable no-script-url, no-console */
const PARAMETER_MAPPING = {
  paymentReferenceNumber: 'paymentReferenceNumber',
  ref: 'ref',
  payment: 'payment',
};

/**
 * Validates if the redirect URL is from the allowed brand redirect URLs
 * @param {string} redirectUrl - The URL to validate
 * @param {object} brandRedirectUrls - Object containing allowed brand redirect URLs
 * @returns {boolean} - True if URL is valid and allowed
 */
export const isValidRedirectUrl = (redirectUrl, brandRedirectUrls) => {
  if (!redirectUrl || typeof redirectUrl !== 'string') {
    return false;
  }

  try {
    const url = new URL(redirectUrl);

    // Check protocol is http or https
    if (!['http:', 'https:'].includes(url.protocol)) {
      return false;
    }

    // Check for javascript: protocol or script tags
    const lowerUrl = redirectUrl.toLowerCase();
    if (
      lowerUrl.includes('javascript:') ||
      lowerUrl.includes('<script') ||
      lowerUrl.includes('</script')
    ) {
      return false;
    }

    // If brandRedirectUrls provided, validate against allowed URLs
    if (brandRedirectUrls && typeof brandRedirectUrls === 'object') {
      const allowedUrls = Object.values(brandRedirectUrls);
      const isAllowed = allowedUrls.some((allowedUrl) => {
        try {
          const allowed = new URL(allowedUrl);
          // Check if protocol, host, and pathname match
          return (
            url.protocol === allowed.protocol &&
            url.host === allowed.host &&
            url.pathname === allowed.pathname
          );
        } catch {
          return false;
        }
      });

      if (!isAllowed) {
        return false;
      }
    }

    return true;
  } catch {
    return false;
  }
};

/**
 * Sanitizes query string values to prevent XSS attacks
 * Only allows alphanumeric characters, hyphens, underscores, @, +, and dots
 * @param {string} value - The value to sanitize
 * @returns {string} - Sanitized value
 */
export const sanitizeQueryValue = (value) => {
  if (!value || typeof value !== 'string') {
    return '';
  }

  const lowerValue = value.toLowerCase();

  // Block script-related patterns
  if (
    lowerValue.includes('<script') ||
    lowerValue.includes('</script') ||
    lowerValue.includes('javascript:') ||
    lowerValue.includes('onerror') ||
    lowerValue.includes('onload')
  ) {
    return '';
  }

  // Allow alphanumeric, hyphens, underscores, @, +, and dots
  // This pattern preserves common email and reference number formats
  return value.replace(/[^a-zA-Z0-9\-_@+.]/g, '');
};

export const redirectToExternal = (redirectUrl, brandRedirectUrls = null) => {
  if (!redirectUrl) {
    return;
  }

  // Validate the redirect URL
  if (!isValidRedirectUrl(redirectUrl, brandRedirectUrls)) {
    return;
  }

  try {
    // Create URL object for safe URL manipulation
    const targetUrl = new URL(redirectUrl);

    // Get source parameters
    const sourceParams = new URLSearchParams(window.location.search);

    // Map and sanitize parameters
    for (const [key, value] of sourceParams.entries()) {
      const mappedKey = PARAMETER_MAPPING[key] || key;
      const sanitizedValue = sanitizeQueryValue(value);

      if (sanitizedValue) {
        targetUrl.searchParams.append(mappedKey, sanitizedValue);
      }
    }

    // Perform redirect
    window.location.href = targetUrl.href;
  } catch (error) {
    console.error('Error during redirect:', error);
  }
};
